import type { Theme } from '@mui/material';
import { Button, Card, CardHeader, Divider, Typography, useMediaQuery } from '@mui/material';
import type { FC } from 'react';
import { useTranslation } from 'react-i18next';

import { PropertyList } from '../../../../../components/atoms/property-list';
import { PropertyListItem } from '../../../../../components/atoms/property-list-item';
import { UpdateDestinationTranslationsDialog } from '../../organisms/dialogs/update-destination-translations-dialog/update-destination-translations-dialog';

import { DestinationTranslations as Translations } from '@/features/destinations/api/types/destination';
import { useDialog } from '@/hooks/use-dialog';

interface DestinationTranslationsProps {
  id: number;
  translations: Translations;
}

export const DestinationTranslations: FC<DestinationTranslationsProps> = props => {
  const { id, translations, ...other } = props;
  const mdDown = useMediaQuery((theme: Theme) => theme.breakpoints.down('md'));

  const align = mdDown ? 'vertical' : 'horizontal';
  const { t } = useTranslation(['destination', 'common']);

  const [
    isOpenUpdateDestinationTranslationsDialog,
    handleOpenUpdateDestinationTranslationsDialog,
    handleCloseUpdateDestinationTranslationsDialog,
  ] = useDialog();

  return (
    <>
      <Card variant="outlined" {...other}>
        <CardHeader
          title={t('destination:translations.cardTitle')}
          action={
            <Button
              onClick={handleOpenUpdateDestinationTranslationsDialog}
              color="primary"
              variant="text"
              size="large">
              {t('common:button.edit')}
            </Button>
          }
        />
        <Divider />
        <PropertyList>
          {translations?.pl && (
            <PropertyListItem
              align={align}
              label={t('destination:translations.polish')}
              value={translations.pl}
              width={200}
            />
          )}
          {translations?.en && (
            <PropertyListItem
              align={align}
              label={t('destination:translations.english')}
              value={translations.en}
              width={200}
            />
          )}
          {translations?.ua && (
            <PropertyListItem
              align={align}
              label={t('destination:translations.ukrainian')}
              value={translations.ua}
              width={200}
            />
          )}
          {!translations?.pl && !translations?.en && !translations?.ua && (
            <Typography sx={{ p: 3 }} variant="inherit">
              {t('destination:translations.noTranslations')}
            </Typography>
          )}
        </PropertyList>
      </Card>

      <UpdateDestinationTranslationsDialog
        open={isOpenUpdateDestinationTranslationsDialog}
        onCancel={handleCloseUpdateDestinationTranslationsDialog}
        destinationId={id}
        translations={translations}
      />
    </>
  );
};
